import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className="container mt-5">
            <h1 className="text-center">Privacy Policy</h1>
            <p>
                Alborz Payment is committed to protecting your privacy and safeguarding your personal information. This Privacy Policy outlines how we collect, use, disclose, and protect your information when you use our services, including through our website (<a href="https://www.alborzpayment.com" target="_blank" rel="noopener noreferrer">www.alborzpayment.com</a>), mobile applications, and customer communications (collectively, the “Services”).
            </p>

            <section>
                <h2>1. Definition of Personal Information</h2>
                <p>
                    “Personal Information” means information about an identifiable individual, excluding business contact information. This includes but is not limited to your name, contact details, government-issued ID, financial details, and IP address.
                </p>
            </section>

            <section>
                <h2>2. Legal Basis and Consent</h2>
                <p>
                    By accessing or using our Services or submitting Personal Information to us, you consent to the collection, use, disclosure, and processing of your information as described in this Privacy Policy. We handle your Personal Information in accordance with Canadian privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA).
                </p>
            </section>

            <section>
                <h2>3. Information We Collect</h2>
                <h3>3.1 Information You Provide</h3>
                <ul>
                    <li>Full name, address, email, phone number</li>
                    <li>Financial and payment information</li>
                    <li>Identity verification documents</li>
                    <li>Personal descriptions or photos (e.g., for KYC compliance)</li>
                </ul>

                <h3>3.2 Technical Information</h3>
                <ul>
                    <li>IP address, device type, browser, OS</li>
                    <li>Login timestamps, location data, page interactions</li>
                </ul>

                <h3>3.3 Information from Third Parties</h3>
                <ul>
                    <li>Identity and payment information from banks or payment processors</li>
                    <li>Authentication data from social media logins (e.g., Facebook, Google)</li>
                    <li>Credit and compliance data from third-party providers</li>
                </ul>
            </section>

            <section>
                <h2>4. Use of Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies, pixel tags, and analytics tools like Google Analytics and Mixpanel to:
                </p>
                <ul>
                    <li>Provide and personalize services</li>
                    <li>Improve performance and security</li>
                    <li>Measure user behavior and ad performance</li>
                </ul>
                <p>
                    Users can manage cookie preferences in their browser settings. Disabling cookies may impact functionality.
                </p>
            </section>

            <section>
                <h2>5. Use of Personal Information</h2>
                <p>We may use your Personal Information to:</p>
                <ul>
                    <li>Fulfill your service requests and process transactions</li>
                    <li>Perform identity verification (KYC/AML)</li>
                    <li>Comply with legal and regulatory obligations</li>
                    <li>Improve our Services and prevent fraud</li>
                    <li>Send you administrative, promotional, or service-related communications (subject to your preferences)</li>
                </ul>
            </section>

            <section>
                <h2>6. Disclosure of Personal Information</h2>
                <p>We may share your Personal Information with:</p>
                <ul>
                    <li>
                        <strong>Service Providers:</strong> KYC/AML verifiers, cloud and infrastructure vendors, payment processors
                    </li>
                    <li>
                        <strong>Business Partners:</strong> Money exchange stores we partner with to facilitate international transactions (see below)
                    </li>
                    <li>
                        <strong>Legal and Regulatory Bodies:</strong> Law enforcement, courts, FINTRAC, and other authorities upon lawful request
                    </li>
                    <li>
                        <strong>Others:</strong> In connection with a merger, sale, or acquisition or when required to enforce our policies or protect rights
                    </li>
                </ul>
                <p>
                    Important Note: Alborz Payment may partner with third-party money exchange stores to deliver certain services. While we perform due diligence, we are not liable for any unlawful activities conducted independently by these partners.
                </p>
            </section>

            <section>
                <h2>7. Data Retention</h2>
                <p>
                    We retain Personal Information only as long as necessary to fulfill its intended purposes and comply with applicable laws, including anti-money laundering recordkeeping requirements.
                </p>
            </section>

            <section>
                <h2>8. Security</h2>
                <p>
                    We use physical, technical, and organizational safeguards to protect your data:
                </p>
                <ul>
                    <li>Secure servers and data encryption</li>
                    <li>Restricted access to sensitive information</li>
                    <li>Routine audits and monitoring</li>
                </ul>
                <p>
                    Despite our efforts, no system is 100% secure. If you suspect a breach, contact us immediately.
                </p>
            </section>

            <section>
                <h2>9. Cross-Border Transfers</h2>
                <p>
                    Your Personal Information may be stored or processed in countries outside Canada (e.g., the United States). While such data may be subject to foreign laws, we ensure a comparable level of protection through contractual and technical safeguards.
                </p>
            </section>

            <section>
                <h2>10. Your Rights</h2>
                <p>You may:</p>
                <ul>
                    <li>Request access to your Personal Information</li>
                    <li>Ask for corrections or deletions</li>
                    <li>Withdraw consent (subject to legal or contractual limitations)</li>
                </ul>
                <p>
                    We may charge a reasonable fee for requests or refuse requests that are excessive or unlawful. We may retain some information to fulfill legal obligations.
                </p>
            </section>

            <section>
                <h2>11. Use of Services by Minors</h2>
                <p>
                    Our Services are not directed to individuals under the age of 18. We do not knowingly collect Personal Information from minors.
                </p>
            </section>

            <section>
                <h2>12. Third-Party Links and Apps</h2>
                <p>
                    Our Services may include links to external websites or apps. We are not responsible for the privacy practices of those parties. Please review their privacy policies.
                </p>
            </section>

            <section>
                <h2>13. Changes to this Policy</h2>
                <p>
                    We may revise this Privacy Policy at any time. The “Last Updated” date at the top indicates the latest version. Continued use of our Services constitutes your acceptance of any changes.
                </p>
            </section>

            <section>
                <h2>14. Contact Us</h2>
                <p>For questions or concerns about this Privacy Policy, please contact:</p>
                <address>
                    <strong>Alborz Payment</strong><br />
                    Email: <a href="mailto:contact@alborzpayment.com">contact@alborzpayment.com</a><br />
                    Phone: +1 (647) 745-4666
                </address>
                <p>Please do not send sensitive information via email.</p>
            </section>
        </div>
    );
};

export default PrivacyPolicy;